import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Shield } from 'lucide-react';
import { SectionSideNav, SectionSideNavItem } from '../SectionSideNav';
import AiAgentChat, { AgentClickPayload, AgentContext } from '../AiAgentChat';
import { useGtmArchitecture, useGtmArchitectureProducts } from '../../../hooks/gtm-architecture-hooks';
import { GtmContentListItem, GtmContentSection } from '../../../types/gtm-architecture';
import {
  DEFAULT_CTA_BUTTONS,
  DEFAULT_HERO_GRADIENT,
  DEFAULT_LIST_LABELS,
  architectureOverride,
} from './architectureOverrides';
import ArchitectureProducts from './ArchitectureProducts';
import ArchitectureLearningJourneys from './ArchitectureLearningJourneys';
import {
  ARCHITECTURE_CONTAINERS,
  BRIEF_PARENTS,
  ComparisonTable,
  ComponentCards,
  CompetitorCards,
  DataError,
  EvaluationSteps,
  FaqAccordion,
  LearnGrid,
  NoContent,
  PointGrid,
  RecognitionsBand,
  ResourceLinkRow,
  SalesProcessDeck,
  SectionHeader,
  SkeletonCards,
  SourcedContentCard,
  StatsBand,
  UseCaseCarousel,
  byOrder,
} from './architectureSections';

/**
 * One data-driven page for every `go_to_market` row of `type = 'architecture'`.
 *
 * The page's shape is the data's shape: it renders the motion's `content_section` rows in their
 * stored order, and each container section pulls in whichever collection belongs to it (use
 * cases, competitors, sales steps, labs, products...). A motion that has no FAQ or no eval
 * process simply has no such section — nothing here assumes a fixed set. Presentation-only
 * details the database has no column for come from the per-slug override.
 */

const HEADING_GRADIENT = {
  background: 'linear-gradient(135deg, #ffffff, #8eafc8)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
} as const;

const LEARN_BADGE_COLOR = '#34d399';

const listItemsOfKind = (section: GtmContentSection, kind: GtmContentListItem['kind']) =>
  byOrder(section.list_items).filter((li) => li.kind === kind);

export interface ArchitecturePageProps {
  slug: string;
}

const ArchitecturePage: React.FC<ArchitecturePageProps> = ({ slug }) => {
  const override = architectureOverride(slug);
  const { data: gtm, isLoading, isError, refetch } = useGtmArchitecture(slug);
  const productData = useGtmArchitectureProducts(gtm);

  const [agentContext, setAgentContext] = useState<AgentContext | null>(null);
  const agentNonceRef = useRef(0);
  const handleAgentClick = (ctx: AgentClickPayload) => {
    agentNonceRef.current += 1;
    setAgentContext({ ...ctx, nonce: agentNonceRef.current });
  };

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [activeSection, setActiveSection] = useState('');

  const sections = useMemo(() => byOrder(gtm?.content_section), [gtm]);
  const heroSection = sections.find((s) => s.key === 'hero');
  const ctaSection = sections.find((s) => s.key === 'cta');

  /** Brief sections render as cards inside their container. */
  const briefsByParent = useMemo(() => {
    const map: Record<string, GtmContentSection[]> = {};
    sections.forEach((s) => {
      const parent = BRIEF_PARENTS[s.key];
      if (!parent) return;
      (map[parent] = map[parent] || []).push(s);
    });
    return map;
  }, [sections]);

  /**
   * A container section is only worth a page section (and a nav dot) if something fills it. A
   * motion that has content rows but no components, no use cases, or no linked products would
   * otherwise show a heading above empty space.
   */
  const hasContent = (section: GtmContentSection): boolean => {
    if (briefsByParent[section.key]?.length) return true;
    const g = gtm as any;
    switch (section.key) {
      case 'architecture':
        return !!override.diagram || !!g?.component?.length;
      case 'usecases':
        return !!g?.use_case?.length;
      case 'competitive':
        return !!(gtm?.competitor?.length || gtm?.comparison_feature?.length);
      case 'eval-process':
        return !!gtm?.evaluation_step?.length;
      case 'sales-process':
        return !!(gtm?.sales_step?.length || listItemsOfKind(section, 'internal_resource').length);
      case 'products':
        // Products are resolved from the motion's components / use cases against the cached
        // product master, so their presence is known without waiting on a request.
        return productData.isLoading || productData.products.length > 0;
      case 'learn':
        return !!(gtm?.lab?.length || gtm?.demo?.length);
      case 'faq':
        return !!gtm?.faq?.length;
      default:
        return !!section.points?.length;
    }
  };

  /** Container sections in stored order — hero and CTA are rendered separately as page furniture. */
  const bodySections = sections.filter(
    (s) =>
      ARCHITECTURE_CONTAINERS[s.key] && s.key !== 'hero' && s.key !== 'cta' && hasContent(s)
  );

  const navSections: SectionSideNavItem[] = bodySections
    .filter((s) => ARCHITECTURE_CONTAINERS[s.key].inNav !== false)
    .map((s) => ({
      id: s.key,
      label: ARCHITECTURE_CONTAINERS[s.key].navLabel,
      icon: ARCHITECTURE_CONTAINERS[s.key].icon,
    }));

  const hasArchitectureSection = bodySections.some((s) => s.key === 'architecture');
  const firstNavId = navSections[0]?.id;

  // The browser tab title is fixed to "Security Hub" by public/index.html. Pages must not
  // mutate `document.title` — it stays the same for every route.

  // react-router client-side navigation doesn't auto-scroll to a URL hash the way a full page
  // load does. Wait for the content to land, since the target section doesn't exist until then.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash || !gtm) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [gtm]);

  // Scroll-spy for the side nav. Re-created when the nav changes, because which sections exist
  // isn't known until the motion's content arrives.
  useEffect(() => {
    if (!navSections.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
    );
    navSections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navSections.map((s) => s.id).join(',')]);

  const scrollToSection = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** The collection a container section owns, below its brief cards. */
  const renderContainerBody = (section: GtmContentSection) => {
    const sectionLabel = section.eyebrow || section.heading || ARCHITECTURE_CONTAINERS[section.key].navLabel;

    switch (section.key) {
      case 'architecture':
        if (override.diagram) {
          return (
            <div className="bg-[#0f2035] rounded-2xl border border-white/10 overflow-hidden mb-12">
              <div className="p-6 sm:p-8">
                <img
                  src={override.diagram.src}
                  alt={override.diagram.alt}
                  className="w-full h-auto rounded-xl"
                />
              </div>
              <div className="px-6 pb-4">
                <p className="text-[#6b8299] text-xs italic">
                  © Cisco and/or its affiliates. All rights reserved.
                </p>
              </div>
            </div>
          );
        }
        return <ComponentCards components={(gtm as any)?.component || []} override={override} />;

      case 'usecases':
        return (
          <UseCaseCarousel useCases={(gtm as any)?.use_case || []} tabs={override.useCaseTabs} />
        );

      case 'competitive':
        return (
          <>
            <ComparisonTable
              vendors={gtm?.comparison_vendor || []}
              features={gtm?.comparison_feature || []}
            />
            <CompetitorCards competitors={gtm?.competitor || []} />
          </>
        );

      case 'eval-process':
        return <EvaluationSteps steps={gtm?.evaluation_step || []} onItemClick={handleAgentClick} />;

      case 'sales-process':
        return (
          <>
            <SalesProcessDeck steps={gtm?.sales_step || []} />
            <ResourceLinkRow
              items={listItemsOfKind(section, 'internal_resource')}
              label={override.listLabels?.internal_resource || DEFAULT_LIST_LABELS.internal_resource}
            />
          </>
        );

      case 'products':
        return <ArchitectureProducts {...productData} onRetry={refetch} />;

      case 'learn':
        return <LearnGrid labs={gtm?.lab || []} demos={gtm?.demo || []} />;

      case 'faq':
        return <FaqAccordion faqs={gtm?.faq || []} />;

      default:
        // `pov` and any future container whose content is just its own points.
        return section.points?.length ? (
          <PointGrid
            points={section.points}
            sectionLabel={sectionLabel}
            onItemClick={handleAgentClick}
          />
        ) : null;
    }
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-[#0b1929] py-32">
        <div className="max-w-[720px] mx-auto px-5">
          <DataError label="this architecture" onRetry={refetch} />
        </div>
      </div>
    );
  }

  if (isLoading || !gtm) {
    return (
      <div className="min-h-screen bg-[#0b1929] py-32">
        <div className="max-w-[1120px] mx-auto px-5 lg:px-10">
          <div className="h-10 w-2/3 bg-white/10 rounded mb-4 animate-pulse" />
          <div className="h-4 w-1/2 bg-white/5 rounded mb-12 animate-pulse" />
          <SkeletonCards count={4} cols="sm:grid-cols-2 lg:grid-cols-4" />
        </div>
      </div>
    );
  }

  // The whole page is content sections, so a payload without them would otherwise render a bare
  // hero and nothing else - which reads as a broken page rather than as missing content.
  if (!sections.length) {
    return (
      <div className="min-h-screen bg-[#0b1929] py-32">
        <div className="max-w-[720px] mx-auto px-5">
          <NoContent name={gtm.name} onRetry={refetch} />
        </div>
      </div>
    );
  }

  const BadgeIcon = override.badgeIcon || Shield;
  const [heroPlainLine, heroAccentLine] = (heroSection?.heading || gtm.name || '')
    .split('|')
    .map((part) => part.trim());
  const ctaButtons = override.ctaButtons || DEFAULT_CTA_BUTTONS;

  return (
    <div className="min-h-screen bg-[#0b1929]">
      {navSections.length > 0 && (
        <SectionSideNav
          sections={navSections}
          activeSection={activeSection || firstNavId || ''}
          onNavigate={scrollToSection}
        />
      )}

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1929] via-[#0f2035] to-[#0b1929]" />
        <div
          className="absolute inset-0 opacity-20"
          style={{ background: 'radial-gradient(ellipse at 50% 120%, #049fd9 0%, transparent 60%)' }}
        />

        <div className="relative max-w-[1120px] mx-auto px-5 lg:px-10 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-8">
            <BadgeIcon className="w-4 h-4 text-[#049fd9]" />
            <span className="text-white text-sm font-medium tracking-wide uppercase">
              {heroSection?.eyebrow || ARCHITECTURE_CONTAINERS.hero.badge}
            </span>
          </div>

          {heroPlainLine && (
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-2">
              {heroPlainLine}
            </h1>
          )}
          {heroAccentLine && (
            <h1
              className="text-5xl md:text-6xl font-extrabold leading-tight mb-8"
              style={{
                background: override.heroGradient || DEFAULT_HERO_GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {heroAccentLine}
            </h1>
          )}

          <p className="text-[#8eafc8] text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            {heroSection?.intro || gtm.description}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {hasArchitectureSection && (
              <a
                href="#architecture"
                className="px-6 py-3 bg-[#049fd9] text-white rounded-lg font-semibold text-sm hover:bg-[#0380b0] transition-colors"
              >
                Explore Architecture
              </a>
            )}
            {firstNavId && (
              <a
                href={`#${firstNavId}`}
                className="px-6 py-3 border border-[#049fd9] text-[#049fd9] rounded-lg font-semibold text-sm hover:bg-[#049fd9]/10 transition-colors"
              >
                {`Start ${ARCHITECTURE_CONTAINERS[firstNavId].navLabel.split(' ')[0]}`}
              </a>
            )}
          </div>
        </div>
      </section>

      <StatsBand stats={gtm.stat || []} />

      {bodySections.map((section, i) => {
        const container = ARCHITECTURE_CONTAINERS[section.key];
        const isLast = i === bodySections.length - 1;
        const briefs = briefsByParent[section.key] || [];
        const isFaq = section.key === 'faq';

        return (
          <React.Fragment key={section.id}>
          {isFaq && <ArchitectureLearningJourneys gtmId={gtm?.id} />}
          <section
            id={section.key}
            ref={(el) => {
              sectionRefs.current[section.key] = el;
            }}
            className={`py-20 scroll-mt-16 ${isLast ? '' : 'border-b border-white/5'}`}
          >
            <div
              className={`${isFaq ? 'max-w-[900px]' : 'max-w-[1120px]'} mx-auto px-5 lg:px-10`}
            >
              <SectionHeader
                badge={section.eyebrow || container.badge}
                icon={container.icon}
                heading={section.heading}
                intro={section.intro}
                badgeColor={section.key === 'learn' ? LEARN_BADGE_COLOR : undefined}
                plainHeading={section.key === 'discovery'}
              />

              {briefs.map((brief) => (
                <SourcedContentCard
                  key={brief.id}
                  section={brief}
                  listLabels={override.listLabels}
                  onItemClick={handleAgentClick}
                />
              ))}

              {renderContainerBody(section)}
            </div>
          </section>
          </React.Fragment>
        );
      })}

      <RecognitionsBand recognitions={gtm.recognition || []} />

      {ctaSection && (
        <section className="py-20">
          <div className="max-w-[900px] mx-auto px-5 lg:px-10 text-center">
            {ctaSection.heading && (
              <h2 className="text-4xl font-bold mb-3" style={HEADING_GRADIENT}>
                {ctaSection.heading}
              </h2>
            )}
            {ctaSection.intro && (
              <p className="text-[#8eafc8] max-w-2xl mx-auto mb-10">{ctaSection.intro}</p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-4">
              {ctaButtons.map((button, i) => {
                const Icon = button.icon;
                const className =
                  i === 0
                    ? 'px-6 py-3 bg-[#049fd9] text-white rounded-lg font-semibold text-sm hover:bg-[#0380b0] transition-colors inline-flex items-center gap-2'
                    : 'px-6 py-3 border border-[#049fd9] text-[#049fd9] rounded-lg font-semibold text-sm hover:bg-[#049fd9]/10 transition-colors inline-flex items-center gap-2';
                const body = (
                  <>
                    {Icon && <Icon className="w-4 h-4" />}
                    {button.label}
                  </>
                );
                return button.url ? (
                  <a
                    key={button.label}
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {body}
                  </a>
                ) : (
                  <button
                    key={button.label}
                    type="button"
                    onClick={() =>
                      handleAgentClick({
                        kind: 'point',
                        text: button.label,
                        detail: ctaSection.intro || undefined,
                        sectionLabel: gtm.name,
                      })
                    }
                    className={className}
                  >
                    {body}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {!bodySections.some((sec) => sec.key === 'faq') && <ArchitectureLearningJourneys gtmId={gtm?.id} />}

      <AiAgentChat trigger={agentContext} />
    </div>
  );
};

export default ArchitecturePage;
